import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Building2, ChevronDown, Landmark, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ESPACES, PAGE_CONNEXION } from "@/content/espaces.meta";
import { APP_LINK_PROPS, track } from "@/lib/config";

/**
 * Les deux portes de connexion de TyCopro, présentées partout de la même façon :
 * barre de navigation (menu), menu mobile, pied de page et page /connexion.
 *
 * Chaque porte se reconnaît à TROIS signes, jamais à un seul : son nom, la personne à
 * qui elle s'adresse, et sa couleur (indigo pour TyCopro, vert pour TyCopro Manager —
 * la couleur que l'on retrouve une fois arrivé dans l'application). Son adresse est
 * écrite en clair : c'est elle que l'on doit retrouver dans la barre d'adresse avant
 * de saisir un mot de passe.
 *
 * Les adresses viennent de content/espaces.meta.js, où elles sont validées.
 */

const APPARENCE = {
    app: {
        Icone: Building2,
        tuile: "bg-primary-700 text-white",
        tuileDouce: "bg-primary-50 text-primary-700",
        titre: "text-primary-800",
        bordure: "border-primary-200",
        survol: "hover:border-primary-300 hover:bg-primary-50/50",
        focus: "focus:bg-primary-50",
        bouton: "bg-primary-700 hover:bg-primary-600 text-white",
        lien: "text-primary-700 hover:text-primary-800",
        halo: "from-primary-300/40 via-primary-200/20",
        pastille: "bg-primary-50 text-primary-800 border-primary-200",
    },
    manager: {
        Icone: Landmark,
        // Aplat vert = texte SOMBRE (le blanc y tombe à 1,9:1).
        tuile: "bg-manager text-manager-foreground",
        tuileDouce: "bg-manager-50 text-manager-800",
        titre: "text-manager-800",
        bordure: "border-manager-200",
        survol: "hover:border-manager-700/60 hover:bg-manager-50/60",
        focus: "focus:bg-manager-50",
        bouton: "bg-manager hover:bg-manager-600 text-manager-foreground",
        lien: "text-manager-800 hover:text-manager-900",
        halo: "from-manager-200/50 via-manager-100/30",
        pastille: "bg-manager-50 text-manager-900 border-manager-200",
    },
};

export const apparenceEspace = (id) => APPARENCE[id] || APPARENCE.app;

function suivre(espaceId, depuis) {
    track("login_clicked", { espace: espaceId, depuis });
}

/** Tuile d'icône colorée de l'espace. */
export function IconeEspace({ id, taille = "md", douce = false }) {
    const a = apparenceEspace(id);
    const Icone = a.Icone;
    const dims = taille === "lg" ? "h-12 w-12 rounded-2xl" : taille === "sm" ? "h-8 w-8 rounded-lg" : "h-10 w-10 rounded-xl";
    const icone = taille === "lg" ? "h-6 w-6" : taille === "sm" ? "h-4 w-4" : "h-5 w-5";
    return (
        <span aria-hidden="true" className={`${dims} ${douce ? a.tuileDouce : a.tuile} grid shrink-0 place-items-center`}>
            <Icone className={icone} strokeWidth={1.75} />
        </span>
    );
}

/** Adresse de l'espace, écrite en clair avec un cadenas : « app.tycopro.fr ». */
export function AdresseEspace({ hote, className = "" }) {
    return (
        <span className={`inline-flex items-center gap-1 font-mono text-[11px] leading-none text-slate-500 ${className}`}>
            <Lock className="h-3 w-3 shrink-0" aria-hidden="true" />
            <span>
                <span className="sr-only">Adresse : </span>
                {hote}
            </span>
        </span>
    );
}

/**
 * Menu « Connexion » de la barre de navigation (ordinateur).
 * Radix gère le clavier (flèches, Entrée, Échap) et rend le focus au bouton à la fermeture.
 */
export function ConnexionMenu() {
    return (
        <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    data-testid="nav-login-btn"
                    className="group gap-1 text-primary-700 hover:bg-primary-50 hover:text-primary-800 data-[state=open]:bg-primary-50"
                >
                    Connexion
                    <ChevronDown className="h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-180" aria-hidden="true" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="end"
                sideOffset={8}
                data-testid="nav-login-menu"
                className="w-[22rem] rounded-2xl border-slate-200 bg-white p-2 shadow-[0_24px_60px_-20px_rgba(30,27,75,0.35)]"
            >
                <DropdownMenuLabel className="px-3 pb-2 pt-1.5 text-xs font-normal text-slate-500">
                    Choisissez votre espace
                </DropdownMenuLabel>
                {ESPACES.map((e) => {
                    const a = apparenceEspace(e.id);
                    return (
                        // Disposition posée sur l'item (fusionnée par tailwind-merge) et non sur le lien :
                        // avec `asChild`, Radix CONCATÈNE les deux className sans arbitrage, et
                        // `items-center` (défaut de l'item) l'emporterait sur un `items-start`.
                        <DropdownMenuItem
                            key={e.id}
                            asChild
                            className={`cursor-pointer items-start gap-3 rounded-xl p-3 focus:text-slate-900 ${a.focus}`}
                        >
                            <a
                                href={e.connexion}
                                {...APP_LINK_PROPS}
                                data-testid={`nav-login-${e.id}`}
                                onClick={() => suivre(e.id, "navbar")}
                            >
                                <IconeEspace id={e.id} />
                                <span className="min-w-0 flex-1">
                                    <span className={`flex items-center gap-1 text-sm font-semibold ${a.titre}`}>
                                        {e.nom}
                                        <ArrowUpRight className="h-3.5 w-3.5 opacity-60" aria-hidden="true" />
                                    </span>
                                    <span className="mt-0.5 block text-[13px] leading-snug text-slate-600">{e.pourQui}</span>
                                    <AdresseEspace hote={e.hote} className="mt-1.5" />
                                </span>
                            </a>
                        </DropdownMenuItem>
                    );
                })}
                <DropdownMenuSeparator className="my-1.5 bg-slate-100" />
                <DropdownMenuItem asChild className="cursor-pointer rounded-xl px-3 py-2 text-xs text-slate-600 focus:bg-slate-50 focus:text-slate-900">
                    <Link to={PAGE_CONNEXION} data-testid="nav-login-help">
                        Quel espace choisir ? Conseils de connexion sécurisée
                    </Link>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

/** Les deux portes dans le menu mobile : des liens simples, lisibles au pouce. */
export function ConnexionListeMobile({ onNavigate }) {
    return (
        <div className="flex flex-col gap-2" data-testid="nav-login-mobile">
            <p className="text-xs uppercase tracking-[0.14em] text-slate-500">Connexion</p>
            {ESPACES.map((e) => {
                const a = apparenceEspace(e.id);
                return (
                    <a
                        key={e.id}
                        href={e.connexion}
                        {...APP_LINK_PROPS}
                        data-testid={`nav-login-mobile-${e.id}`}
                        onClick={() => {
                            suivre(e.id, "navbar_mobile");
                            onNavigate?.();
                        }}
                        className={`flex min-h-[3.25rem] items-center gap-3 rounded-xl border bg-white px-3 py-2.5 transition-colors ${a.bordure} ${a.survol}`}
                    >
                        <IconeEspace id={e.id} taille="sm" />
                        <span className="min-w-0 flex-1">
                            <span className={`block text-sm font-semibold ${a.titre}`}>{e.nom}</span>
                            <span className="block text-xs text-slate-500">{e.titre}</span>
                        </span>
                        <ArrowUpRight className="h-4 w-4 text-slate-400" aria-hidden="true" />
                    </a>
                );
            })}
        </div>
    );
}

/**
 * Grande carte d'un espace (page /connexion). Le bouton principal ouvre la page de
 * connexion de l'application ; le lien secondaire dépend de l'espace (inscription en
 * ligne pour TyCopro, présentation de Manager — qui n'a PAS d'inscription en ligne).
 */
export function CarteEspace({ espace: e }) {
    const a = apparenceEspace(e.id);
    return (
        <article
            data-testid={`espace-${e.id}`}
            className={`relative isolate flex flex-col overflow-hidden rounded-3xl border bg-white p-6 sm:p-8 shadow-[0_20px_50px_-30px_rgba(15,23,42,0.35)] ${a.bordure}`}
        >
            <div
                aria-hidden="true"
                className={`absolute -right-16 -top-16 -z-10 h-56 w-56 rounded-full bg-gradient-to-br ${a.halo} to-transparent blur-2xl`}
            />
            <div className="flex items-center gap-3">
                <IconeEspace id={e.id} taille="lg" />
                <div>
                    <p className={`text-sm font-semibold ${a.titre}`}>{e.nom}</p>
                    <h2 className="font-heading text-xl sm:text-2xl tracking-tight text-slate-900">{e.titre}</h2>
                </div>
            </div>

            <p className="mt-5 text-base text-slate-700">{e.pourQui}</p>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">{e.detail}</p>

            <p className={`mt-5 inline-flex max-w-full flex-wrap items-center gap-x-2 gap-y-1 self-start rounded-xl border px-3 py-2 text-xs ${a.pastille}`}>
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                    <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    Adresse à vérifier :
                </span>
                <strong className="break-all font-mono font-semibold">https://{e.hote}</strong>
            </p>

            <div className="mt-auto pt-7 flex flex-col gap-3">
                <Button asChild className={`h-12 rounded-xl text-base font-semibold shadow-sm ${a.bouton}`}>
                    <a
                        href={e.connexion}
                        {...APP_LINK_PROPS}
                        data-testid={`espace-${e.id}-connexion`}
                        onClick={() => suivre(e.id, "page_connexion")}
                    >
                        Se connecter à {e.nom}
                        <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                    </a>
                </Button>
                {e.inscription ? (
                    <p className="text-sm text-slate-600">
                        {e.noteInscription}{" "}
                        <a
                            href={e.inscription}
                            {...APP_LINK_PROPS}
                            onClick={() => track("cta_clicked", { cta: "page_connexion" })}
                            className={`font-semibold underline-offset-4 hover:underline ${a.lien}`}
                        >
                            Démarrer
                        </a>
                    </p>
                ) : (
                    <p className="text-sm text-slate-600">
                        {e.noteInscription}{" "}
                        {e.page && (
                            <Link to={e.page} className={`font-semibold underline-offset-4 hover:underline ${a.lien}`}>
                                Découvrir {e.nom}
                            </Link>
                        )}
                    </p>
                )}
            </div>
        </article>
    );
}
